import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, MapPin, Send, RefreshCw, X, HardHat, FolderCheck,
  FolderClock, FolderX, AlertTriangle, Mail, Check, Trash2,
} from 'lucide-react';

const CARD = 'bg-[#2A3240] border-white/10';

function PhotoThumb({ api, captureId, photo, onOpen }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj;
    let active = true;
    api.get(`/bc/captures/${captureId}/photo/${photo.id}?thumb=1`, { responseType: 'blob' })
      .then(r => { if (active) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
      .catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [api, captureId, photo.id]);
  return (
    <button onClick={() => onOpen(photo)} className="relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-[#1E2530]" data-testid={`bc-photo-${photo.id}`}>
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <Loader2 className="w-5 h-5 animate-spin text-gray-500 m-auto absolute inset-0" />}
      {photo.sent && <span className="absolute bottom-1 left-1 text-[9px] bg-emerald-600/90 text-white px-1 rounded">SENT</span>}
    </button>
  );
}

function Lightbox({ api, captureId, photo, onClose }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj; let active = true;
    const variant = photo.sent ? 'sent' : 'stamped';
    api.get(`/bc/captures/${captureId}/photo/${photo.id}?variant=${variant}`, { responseType: 'blob' })
      .then(r => { if (active) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
      .catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [api, captureId, photo]);
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="bc-lightbox">
      <button className="absolute top-4 right-4 text-white/80" onClick={onClose}><X className="w-6 h-6" /></button>
      {url ? <img src={url} alt="" className="max-w-full max-h-full object-contain rounded-lg" /> : <Loader2 className="w-8 h-8 animate-spin text-white" />}
    </div>
  );
}

export function BCCaptureView({ captureId, onChanged, onDeleted, embedded = false }) {
  const { api } = useAuth();
  const [cap, setCap] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [errorNote, setErrorNote] = useState('');
  const [showErrorInput, setShowErrorInput] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([api.get(`/bc/captures/${captureId}`), api.get('/bc/me')]);
      setCap(c.data); setMe(m.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load capture'); }
    finally { setLoading(false); }
  }, [api, captureId]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this Building Control capture? This permanently removes its photos and its Site Diary entry. Anything already filed to SharePoint stays there.')) return;
    setBusy(true);
    try {
      await api.delete(`/bc/captures/${captureId}`);
      toast.success('Capture deleted');
      onChanged?.();
      onDeleted?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>;
  if (!cap) return <p className="text-gray-400 p-6">Capture not found.</p>;

  const fc = cap.folder_copy || {};
  const isSend = cap.kind === 'send';
  const canSend = me?.can_send;
  const isAdmin = me?.is_admin;
  const isOwner = me && cap.created_by === (me.user_id || undefined); // owner check handled server-side too

  const folderTone = fc.status === 'done' ? ['text-green-400', FolderCheck, 'Filed to SharePoint']
    : fc.status === 'pending_preview' ? ['text-amber-400', FolderClock, 'Pending — not run in preview (files on the live app)']
    : fc.status === 'blocked' ? ['text-gray-400', FolderX, 'No project SharePoint folder yet']
    : ['text-red-400', FolderX, fc.reason || 'Filing failed'];

  const deliveryTone = cap.delivery === 'sent' ? ['text-green-400', 'Sent to ' + (cap.recipient_email || 'contact')]
    : cap.delivery === 'queued' ? ['text-amber-400', 'Queued — email not live yet']
    : cap.delivery === 'discarded' ? ['text-gray-400', 'Discarded']
    : cap.delivery === 'failed' ? ['text-red-400', cap.delivery_reason || 'Send failed']
    : ['text-gray-400', '—'];

  return (
    <div className={embedded ? '' : 'p-4 pb-28 max-w-2xl mx-auto'} data-testid="bc-detail">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-mono font-bold text-[#F5A623]">{cap.project_ref}</p>
          <p className="text-white font-semibold">{cap.stage}</p>
          <p className="text-gray-400 text-xs">{cap.project_name} · {(cap.created_at || '').slice(0, 16).replace('T', ' ')} · {cap.created_by_name}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className={`text-[10px] ${isSend ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-gray-300'}`}>{isSend ? 'Sent' : 'Saved'}</Badge>
          {cap.in_error && <Badge className="text-[10px] bg-red-500/20 text-red-300" data-testid="bc-in-error-badge">In error</Badge>}
        </div>
      </div>

      {cap.in_error && cap.in_error_note && (
        <Card className="bg-red-500/10 border-red-500/30 mb-3"><CardContent className="p-3 text-sm text-red-200">
          <span className="font-semibold">Flagged in error:</span> {cap.in_error_note} <span className="text-red-300/70">— {cap.in_error_by}</span>
        </CardContent></Card>
      )}

      {cap.note && <Card className={`${CARD} mb-3`}><CardContent className="p-3 text-sm text-gray-200">{cap.note}</CardContent></Card>}

      {/* status strip */}
      <Card className={`${CARD} mb-3`}>
        <CardContent className="p-3 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <MapPin className={`w-4 h-4 ${cap.gps?.unavailable ? 'text-gray-500' : 'text-green-400'}`} />
            {cap.gps?.unavailable ? <span className="text-gray-400">GPS unavailable</span> :
              <span className="text-gray-200">{cap.gps?.lat?.toFixed(5)}, {cap.gps?.lng?.toFixed(5)}{cap.gps?.address ? ` — ${cap.gps.address}` : ''}</span>}
          </div>
          <div className="flex items-center gap-2">
            {(() => { const [tone, Icon, label] = folderTone; return <><Icon className={`w-4 h-4 ${tone}`} /><span className={tone} data-testid="bc-folder-status">{label}</span></>; })()}
            {fc.status === 'failed' && (
              <button disabled={busy} onClick={() => act(() => api.post(`/bc/captures/${cap.id}/retry-folder`), 'Filing retried')} className="ml-auto text-[#F5A623] text-xs hover:underline flex items-center gap-1" data-testid="bc-retry-folder"><RefreshCw className="w-3 h-3" /> Retry</button>
            )}
          </div>
          {isSend && (
            <div className="flex items-center gap-2">
              <Mail className={`w-4 h-4 ${deliveryTone[0]}`} />
              <span className={deliveryTone[0]} data-testid="bc-delivery-status">{deliveryTone[1]}</span>
              {(cap.delivery === 'failed' || cap.delivery === 'queued') && canSend && (
                <button disabled={busy} onClick={() => act(() => api.post(`/bc/captures/${cap.id}/retry-send`), 'Send retried')} className="ml-auto text-[#F5A623] text-xs hover:underline flex items-center gap-1" data-testid="bc-retry-send"><RefreshCw className="w-3 h-3" /> {cap.delivery === 'queued' ? 'Send now' : 'Retry'}</button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* photos */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {(cap.photos || []).map(p => <PhotoThumb key={p.id} api={api} captureId={cap.id} photo={p} onOpen={setLightbox} />)}
      </div>

      {/* actions */}
      <div className="space-y-2">
        {!isSend && canSend && (
          <div className="space-y-1">
            <Button disabled={busy || !cap.project_has_bc_contact}
              onClick={() => act(() => api.post(`/bc/captures/${cap.id}/send`, {}), 'Sent to Building Control')}
              className="w-full bg-emerald-600 text-white hover:bg-emerald-500 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              title={cap.project_has_bc_contact ? 'Send to Building Control' : 'Assign a Building Control contact to this project first'}
              data-testid="bc-send-saved">
              <Send className="w-4 h-4 mr-2" /> {cap.project_has_bc_contact ? 'Send these photos to Building Control' : 'Send disabled — no Building Control contact'}
            </Button>
            {!cap.project_has_bc_contact && (
              <p className="text-[11px] text-amber-400/80" data-testid="bc-send-saved-hint">
                Ask an admin to assign a Building Control contact to this project (Settings → Building Control → Project contacts).
              </p>
            )}
          </div>
        )}
        {isAdmin && !cap.in_error && (
          showErrorInput ? (
            <div className="flex gap-2">
              <input value={errorNote} onChange={e => setErrorNote(e.target.value)} placeholder="Why is this in error?" className="flex-1 rounded-lg bg-[#1E2530] border border-white/10 text-white text-sm px-3" data-testid="bc-error-note" />
              <Button disabled={busy || !errorNote.trim()} onClick={() => act(() => api.post(`/bc/captures/${cap.id}/mark-error`, { note: errorNote.trim() }), 'Flagged in error')} className="bg-red-600 text-white hover:bg-red-500" data-testid="bc-error-confirm">Flag</Button>
              <Button variant="ghost" onClick={() => setShowErrorInput(false)} className="text-gray-400">Cancel</Button>
            </div>
          ) : (
            <button onClick={() => setShowErrorInput(true)} className="text-red-400/80 text-xs hover:underline flex items-center gap-1" data-testid="bc-mark-error"><AlertTriangle className="w-3 h-3" /> Flag this record in error</button>
          )
        )}
        {isAdmin && (
          <button onClick={handleDelete} disabled={busy} className="text-red-400/80 text-xs hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="bc-delete-capture">
            <Trash2 className="w-3 h-3" /> Delete this capture
          </button>
        )}
      </div>

      {lightbox && <Lightbox api={api} captureId={cap.id} photo={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

export function BuildingControlDetail() {
  const { captureId } = useParams();
  const navigate = useNavigate();
  return (
    <div className="p-4 pb-28 max-w-2xl mx-auto" data-testid="bc-detail-page">
      <button onClick={() => navigate('/portal/building-control')} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-3" data-testid="bc-detail-back">
        <ArrowLeft className="w-4 h-4" /> Building Control
      </button>
      <BCCaptureView captureId={captureId} embedded onDeleted={() => navigate('/portal/building-control')} />
    </div>
  );
}

export default BuildingControlDetail;
